import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {lazy, Suspense, useMemo} from 'react'

import {ErrorFallback} from './ErrorFallback'
import styles from './HybridMarkdownEditor.module.css'
import {SuspenseFallback} from './SuspenseFallback'
import {HybridEditorTelemetryProvider} from './telemetry'
import type {HybridMarkdownEditorProps} from './types'

const CodeMirrorEditor = lazy(async () => {
  const module = await import('./CodeMirrorEditor')
  return {default: module.CodeMirrorEditor}
})

// IMPORTANT: Keep this component as minimal as possible, with no logic that might possibly throw an error, since any
// errors thrown in this component wouldn't be caught by the error boundary
export function HybridMarkdownEditor({
  onChangeViewMode,
  value,
  placeholder,
  ref,
  ...restProps
}: HybridMarkdownEditorProps) {
  return (
    <div className={styles.container}>
      <ErrorBoundary fallback={<ErrorFallback onChangeViewMode={onChangeViewMode} />}>
        <HybridEditorTelemetryProvider
          options={useMemo(() => ({featureFlagEnabled: isFeatureEnabled('markdown_experience_hybrid_editor')}), [])}
        >
          <Suspense fallback={<SuspenseFallback value={value} placeholder={placeholder} />}>
            <CodeMirrorEditor
              ref={ref}
              onChangeViewMode={onChangeViewMode}
              value={value}
              placeholder={placeholder}
              {...restProps}
            />
          </Suspense>
        </HybridEditorTelemetryProvider>
      </ErrorBoundary>
    </div>
  )
}

try{ CodeMirrorEditor.displayName ||= 'CodeMirrorEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}